import cx from 'classnames';
import {
  useCallback,
  useContext,
  useRef,
  useState,
  type FunctionComponent,
} from 'react';

import { useIsTemplateCard } from '@trello/business-logic-react/card';
import { useFeatureGate } from '@trello/feature-gate-client';
import { useCardId } from '@trello/id-context';
import { useIsInboxBoard } from '@trello/personal-workspace';
import { useSharedStateSelector } from '@trello/shared-state';

import { useIsCardMarkedComplete } from 'app/src/components/CardDoneState/useIsCardMarkedComplete';
import { useIsInboxAutoArchiveEnabled } from 'app/src/components/CardDoneState/useIsInboxAutoArchiveEnabled';
import type { CardType } from 'app/src/components/CardType';
import { MirrorCardActions } from 'app/src/components/MirrorCard';
import { usePlannerFeatureGate } from 'app/src/components/Planner/usePlannerFeatureGate';
import { activeCardSharedState } from './activeCardSharedState';
import { CardFrontArchiveButton } from './CardFrontArchiveButton';
import { useCardFrontCardInfoFragment } from './CardFrontCardInfoFragment.generated';
import { CardFrontContext } from './CardFrontContext';
import { CardFrontEditButton } from './CardFrontEditButton';
import { CardFrontUnlinkButton } from './CardFrontUnlinkButton';

// eslint-disable-next-line @trello/less-matches-component
import * as styles from './CardFront.module.less';

interface CardFrontActionsProps {
  cardType: CardType;
}

export const CardFrontActions: FunctionComponent<CardFrontActionsProps> = ({
  cardType,
}) => {
  const { isPlannerCardActionsEnabled } = usePlannerFeatureGate();
  const { value: isMirrorCardsEnabled } = useFeatureGate('xf_mirror_cards');
  const [hasOpenPopover, setHasOpenPopover] = useState(false);

  const { editButtonRef, cardFrontSource } = useContext(CardFrontContext);

  const cardId = useCardId();
  const isCardActive = useSharedStateSelector(
    activeCardSharedState,
    useCallback(({ idActiveCard }) => idActiveCard === cardId, [cardId]),
  );

  const { data: card } = useCardFrontCardInfoFragment({
    from: { id: cardId },
  });

  const cardName = card?.name;
  const cardRole = card?.cardRole;
  const isComplete = useIsCardMarkedComplete(cardId);
  const isArchived = card?.closed;
  const isTemplate = useIsTemplateCard(cardId);
  const isInbox = useIsInboxBoard();
  const isInboxAutoArchiveEnabled = useIsInboxAutoArchiveEnabled();
  const previousIsComplete = useRef(isComplete);

  // Determine if the archive button should be hidden while auto archiving *before* we update the ref
  const shouldHideArchiveButtonWhileAutoArchiving =
    isComplete &&
    !previousIsComplete.current &&
    isInbox &&
    isInboxAutoArchiveEnabled;

  previousIsComplete.current = isComplete;

  const isArchiveButtonVisible =
    !shouldHideArchiveButtonWhileAutoArchiving &&
    isComplete &&
    !isArchived &&
    !isTemplate &&
    cardType !== 'separator';

  const isEditButtonVisible = !isArchived;
  const canShowArchiveButtonOnMirrorCard = !isArchived;
  const isPlannerCard = cardFrontSource === 'Planner';

  if (isPlannerCard && !isPlannerCardActionsEnabled) {
    return;
  }

  return (
    <div
      // Because we disable focus management inside planner cards, the action buttons will unmount when the user opens the
      // unlink confirmation popover, which causes it to reposition to the top left corner of the screen. This is a workaround
      // to keep the buttons from unmounting until we find a solution to the jitter bug that doesn't require disabling focus
      // management.
      className={cx(
        styles.cardFrontActions,
        (isCardActive || hasOpenPopover) &&
          styles['cardFrontActions--isCardActive'],
      )}
    >
      {cardType === 'mirror' && isMirrorCardsEnabled && (
        <MirrorCardActions
          canShowArchiveButton={canShowArchiveButtonOnMirrorCard}
        />
      )}
      {isArchiveButtonVisible && <CardFrontArchiveButton />}
      {isPlannerCard && (
        <CardFrontUnlinkButton onPopoverStateChange={setHasOpenPopover} />
      )}
      {isEditButtonVisible && (
        <CardFrontEditButton
          ref={editButtonRef}
          cardRole={cardRole}
          cardName={cardName}
        />
      )}
    </div>
  );
};
